import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../../../atoms';

export type ToastTone = 'info' | 'success' | 'warn' | 'error';

export type ToastPayload = {
  id: string;
  tone?: ToastTone;
  title: string;
  desc?: string;
  duration?: number;
};

type Ctx = {
  toasts: ToastPayload[];
  show: (t: Omit<ToastPayload, 'id'>) => string;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<Ctx | null>(null);

const NOOP_CTX: Ctx = {
  toasts: [],
  show: () => '',
  dismiss: () => {},
};

export function useToast() {
  // Outside a provider (e.g. a standalone Storybook preview of a page
  // that doesn't wrap its own app shell) we return a silent no-op so the
  // page still renders and clicks don't throw.
  return useContext(ToastContext) ?? NOOP_CTX;
}

/* Tint layer sits on top of a solid bg-2 so toasts are fully opaque
 * against the page behind. linear-gradient(rgba, rgba) paints a flat
 * tone-tinted wash without hiding the surface colour underneath. */
const TONE_BG: Record<ToastTone, string> = {
  info: 'var(--bg-2)',
  success: 'linear-gradient(rgba(80,200,130,0.12), rgba(80,200,130,0.12)), var(--bg-2)',
  warn: 'linear-gradient(rgba(240,180,80,0.12), rgba(240,180,80,0.12)), var(--bg-2)',
  error: 'linear-gradient(rgba(255,93,93,0.12), rgba(255,93,93,0.12)), var(--bg-2)',
};
const TONE_BORDER: Record<ToastTone, string> = {
  info: 'var(--line-3)',
  success: 'rgba(80, 200, 130, 0.35)',
  warn: 'rgba(240, 180, 80, 0.35)',
  error: 'rgba(255, 93, 93, 0.35)',
};
const TONE_COLOR: Record<ToastTone, string> = {
  info: 'var(--brand)',
  success: 'var(--up)',
  warn: 'var(--sev-med)',
  error: 'var(--dn)',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastPayload[]>([]);
  const timers = useRef<Record<string, number>>({});

  const dismiss = useCallback((id: string) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
    const h = timers.current[id];
    if (h) window.clearTimeout(h);
    delete timers.current[id];
  }, []);

  const show = useCallback<Ctx['show']>(
    (t) => {
      const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const full: ToastPayload = { id, tone: 'info', duration: 4000, ...t };
      setToasts((ts) => [...ts, full]);
      if (full.duration && full.duration > 0) {
        timers.current[id] = window.setTimeout(() => dismiss(id), full.duration);
      }
      return id;
    },
    [dismiss]
  );

  useEffect(() => {
    return () => {
      Object.values(timers.current).forEach((h) => window.clearTimeout(h));
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, show, dismiss }}>
      {children}
      <ToastHost />
    </ToastContext.Provider>
  );
}

function ToastHost() {
  const { toasts, dismiss } = useToast();
  return (
    <div
      className="frr-toast-host"
      style={{
        position: 'fixed',
        top: '12.5%',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        pointerEvents: 'none',
        width: 'min(420px, calc(100% - 32px))',
      }}
    >
      <style>{`
        @media (max-width: 640px) {
          .frr-toast-host { top: 16px !important; }
        }
      `}</style>
      {toasts.map((t) => {
        const tone = t.tone ?? 'info';
        return (
          <div
            key={t.id}
            role="status"
            style={{
              pointerEvents: 'auto',
              minWidth: 280,
              maxWidth: 420,
              padding: '12px 14px',
              border: `1px solid ${TONE_BORDER[tone]}`,
              borderLeft: `3px solid ${TONE_COLOR[tone]}`,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              color: 'var(--fg-hi)',
              background: TONE_BG[tone],
              boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
              animation: 'frr-toast-in 220ms cubic-bezier(0.2, 0.85, 0.3, 1)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg-max)' }}>{t.title}</div>
              {t.desc && (
                <div style={{ marginTop: 3, fontSize: 12, color: 'var(--fg-body)', lineHeight: 1.5 }}>{t.desc}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              style={{
                width: 22,
                height: 22,
                display: 'grid',
                placeItems: 'center',
                background: 'transparent',
                color: 'var(--fg-meta)',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <Icon name="close" color="currentColor" size={9} />
            </button>
          </div>
        );
      })}
      <style>{`
        @keyframes frr-toast-in {
          from { transform: translateY(8px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
